/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable max-statements */
import React, { useEffect, useRef, useState } from 'react';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler,
	ScriptableContext,
	ChartData,
	CoreChartOptions,
	DatasetChartOptions,
	ElementChartOptions,
	PluginChartOptions,
	ScaleChartOptions,
	LineControllerChartOptions
} from 'chart.js';
import {
	Line,
	getDatasetAtEvent,
	getElementAtEvent,
	getElementsAtEvent
} from 'react-chartjs-2';
import { useDispatch } from 'react-redux';
import { hideSearchbarAction } from '../../../redux/actions/searchBarAction';
import {
	setSelectedSection,
	setTab
} from '../../../redux/actions/sideBarAction';
import { useQuery } from 'react-query';
import { globalEnv } from '../../../config/baseConfig';
import { useTypedSelector } from '../../../hooks/useTypeSelector';
import LoadingAnimComponent from '../../Utils/LoadingAnimations/loadingAnimation';
import { DataNotFoundAnimation } from '../../Utils/ErrorAnimations/dataNotFoundAnim';
import moment from 'moment';
import LazyImage from '../../Utils/Components/imageComponent';

import cycle from '../../../assets/images/analytics/cycle.png';
import users from '../../../assets/images/analytics/users.png';
import { axiosInstance } from '../../../config/axiosInstance';
import { useParams } from 'react-router-dom';
import { _DeepPartialObject } from 'chart.js/types/utils';
import { text } from 'stream/consumers';
import { DatePickerComponent } from '../../Utils/Components/datePicker/datePicker';
import ReactDatePicker from 'react-datepicker';
import CohortsView from './cohorts';

type RetentionItemType = {
	signupDate: Date;
	activeUsersCount: number;
	inactiveUsersCount: number;
	retentionRate?: number;
};

export type RetentionCohortsType = {
	retentionData: number[];
	total: number;
};

type RetentionRatePerPeriod = {
	activeUsersCount: number;
	inactiveUsersCount: number;
	retentionRate: number;
};

type DynamicStatsType = {
	retention: RetentionItemType[];
	retentionCohorts: RetentionCohortsType[];
};

type DailyStatsType = {
	installCount: number;
	uninstallCount: number;
	inactiveUsersCount: number;
	inactiveUsersAvgTime: number;
	netActiveUsersCount: number;
	numberOfDailyActiveUsers: number;
	createdNotesCount: number;
	createdTodoItemsCount: number;
	totalNumberOfUsers: number;
	createdAt?: Date;
};

export default function Analytics() {
	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});
	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	const { id } = useParams();

	const today = new Date();
	const [startDate, setStartDate] = useState(
		moment(today).subtract(7, 'days').format('YYYY-MM-DD')
	);
	const [endDate, setEndDate] = useState(moment(today).format('YYYY-MM-DD'));
	const [period, setPeriod] = useState<string>('month');
	const [labels, setLabels] = useState<string[]>([]);
	const [showMore, setShowMore] = useState(false);

	const dispatch = useDispatch();
	if (id != null) dispatch(setTab('extensions'));
	else dispatch(setTab('dashboard'));

	dispatch(setSelectedSection('Global Stats'));
	dispatch(hideSearchbarAction());

	const [periodicStats, setPeriodicStats] = useState<DailyStatsType[]>([]);
	const [latestStats, setLatestStats] = useState<DailyStatsType>({
		installCount: 0,
		uninstallCount: 0,
		totalNumberOfUsers: 0,
		inactiveUsersCount: 0,
		inactiveUsersAvgTime: 0,
		netActiveUsersCount: 0,
		numberOfDailyActiveUsers: 0,
		createdNotesCount: 0,
		createdTodoItemsCount: 0
	});

	const [dynamicStats, setDynamicStats] = useState<DynamicStatsType>();
	const [retentionRatePerPeriod, setRetentionRatePerPeriod] =
		useState<RetentionRatePerPeriod>();

	useEffect(() => {
		switch (period) {
			case 'custom': {
				break;
			}
			case 'month': {
				const newStartDate = moment(today).subtract(1, 'months');
				setStartDate(newStartDate.format('YYYY-MM-DD'));
				setEndDate(moment().format('YYYY-MM-DD'));
				break;
			}
			case 'threeMonths': {
				const newStartDate = moment(today).subtract(3, 'months');
				setStartDate(newStartDate.format('YYYY-MM-DD'));
				setEndDate(moment().format('YYYY-MM-DD'));
				break;
			}
			case 'sixMonths': {
				const newStartDate = moment(today).subtract(6, 'months');
				setStartDate(newStartDate.format('YYYY-MM-DD'));
				setEndDate(moment().format('YYYY-MM-DD'));
				break;
			}
			case 'year': {
				const newStartDate = moment(today).subtract(1, 'years');
				setStartDate(newStartDate.format('YYYY-MM-DD'));
				setEndDate(moment().format('YYYY-MM-DD'));
				break;
			}
			default: {
				const newStartDate = moment(today).subtract(7, 'days');
				setStartDate(newStartDate.format('YYYY-MM-DD'));
				setEndDate(moment().format('YYYY-MM-DD'));
				break;
			}
		}
	}, [period]);

	async function fetchAnalyticsData() {
		let url = 'analytics';
		if (id != null) {
			url = `extension/${id}`;
		}

		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data } = await axiosInstance.get(
			`${globalEnv.BASE_URL}/${url}/get-general-stats?startDate=${startDate}&endDate=${endDate}`,
			config
		);
		return data;
	}

	const { isLoading, isFetching, isError } = useQuery(
		['analytics', startDate, endDate],
		() => fetchAnalyticsData(),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 5000,

			onSuccess: (data) => {
				setPeriodicStats(data.periodicStats ?? []);
				if (data.latestStats) setLatestStats(data.latestStats);
				setDynamicStats(data.dynamicStats);

				const dates: any = [];
				data.periodicStats.forEach((item: any) => {
					let date;

					switch (period) {
						case 'month': {
							date = moment(item.createdAt).format('MMM-DD');
							break;
						}
						case 'threeMonths': {
							date = moment(item.createdAt).format('MMM-DD-YYYY');
							break;
						}
						case 'sixMonths': {
							date = moment(item.createdAt).format('MMM-DD-YYYY');
							break;
						}
						case 'year': {
							date = moment(item.createdAt).format('MMM-YYYY');
							break;
						}
						default: {
							date = moment(item.createdAt).format('MMM-DD');
							break;
						}
					}

					dates.push(date);
				});

				setLabels(dates);
			}
		}
	);

	ChartJS.register(
		CategoryScale,
		LinearScale,
		PointElement,
		LineElement,
		Filler,
		Title,
		Tooltip,
		Legend
	);

	const getTotalUserCount = () => {
		const data = periodicStats.map((item) => item.totalNumberOfUsers);
		data.push(latestStats.totalNumberOfUsers);
		return data;
	};
	const getActiveUserCount = () => {
		const data = periodicStats.map((item) => item.numberOfDailyActiveUsers);
		data.push(latestStats.numberOfDailyActiveUsers);
		return data;
	};
	const getInactiveUserCount = () => {
		const data = periodicStats.map((item) => item.inactiveUsersCount);
		data.push(latestStats.inactiveUsersCount);
		return data;
	};
	const getNetActiveUsersCount = () => {
		const data = periodicStats.map((item) => item.netActiveUsersCount);
		data.push(latestStats.netActiveUsersCount);
		return data;
	};
	const getCreatedTodoItemsCount = () => {
		const data = periodicStats.map((item) => item.createdTodoItemsCount);
		data.push(latestStats.createdTodoItemsCount);
		return data;
	};
	const getCreatedNotesCount = () => {
		const data = periodicStats.map((item) => item.createdNotesCount);
		data.push(latestStats.createdNotesCount);
		return data;
	};
	const getInstallsCount = () => {
		const data = periodicStats.map((item) => item.installCount);
		data.push(latestStats.installCount);
		return data;
	};
	const getUninstallsCount = () => {
		const data = periodicStats.map((item) => item.uninstallCount);
		data.push(latestStats.uninstallCount);
		return data;
	};
	const getRetentionRate = () => {
		if (dynamicStats != null) {
			const data = dynamicStats.retention.map((item) => item.retentionRate);
			return data;
		}
	};

	useEffect(() => {
		if (dynamicStats != null) {
			const initialValue = 0;
			const activeUsersCount = dynamicStats.retention.reduce(
				(acc, currentValue) => acc + currentValue.activeUsersCount,
				initialValue
			);
			const inactiveUsersCount = dynamicStats.retention.reduce(
				(acc, currentValue) => acc + currentValue.inactiveUsersCount,
				initialValue
			);

			const totalUsersCount = activeUsersCount + inactiveUsersCount;

			const retentionRate = (activeUsersCount / totalUsersCount) * 100;
			setRetentionRatePerPeriod({
				activeUsersCount: activeUsersCount,
				inactiveUsersCount: inactiveUsersCount,
				retentionRate: retentionRate
			});
		}
	}, [periodicStats]);

	const getInactiveUsersAvgTimeCount = () => {
		const data = periodicStats.map((item) =>
			Number(item.inactiveUsersAvgTime).toFixed(2)
		);
		data.push(latestStats.inactiveUsersAvgTime.toFixed(2));
		return data;
	};

	const totalNumberOfUsersOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: 'Total number of users'
			}
		}
	};

	const totalNumberOfUsersData = {
		labels,
		datasets: [
			{
				fill: true,
				lineTension: 0.3,
				data: getTotalUserCount(),
				borderColor: 'rgb(33, 202, 155)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(33, 202, 155,0.6)');
					gradient.addColorStop(1, 'rgba(33, 202, 155,0.03)');
					return gradient;
				}
			}
		]
	};

	const numberOfDailyActiveUsersOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: 'Daily active users count'
			}
		}
	};

	const numberOfDailyActiveUsersData = {
		labels,
		datasets: [
			{
				lineTension: 0.3,
				fill: true,
				data: getActiveUserCount(),
				borderColor: 'rgb(53, 102, 235)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(53, 102, 235,0.6)');
					gradient.addColorStop(1, 'rgba(53, 102, 235,0.03)');
					return gradient;
				}
			}
		]
	};

	const inactiveUsersCountOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: 'Total inactive users count'
			}
		}
	};

	const inactiveUsersCountData = {
		labels,
		datasets: [
			{
				lineTension: 0.3,
				fill: true,
				data: getInactiveUserCount(),
				borderColor: 'rgb(255, 49, 82)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(255, 49, 82,0.6)');
					gradient.addColorStop(1, 'rgba(255, 49, 82,0.03)');
					return gradient;
				}
			}
		]
	};

	const inactiveUsersAvgTimeOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: 'Inactive users average time on Meavana (days)'
			}
		}
	};

	const inactiveUsersAvgTimeData = {
		labels,
		datasets: [
			{
				lineTension: 0.3,
				fill: true,
				data: getInactiveUsersAvgTimeCount(),
				borderColor: 'rgb(255, 200, 52)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(255, 200, 52,0.6)');
					gradient.addColorStop(1, 'rgba(255, 200, 52,0.03)');
					return gradient;
				}
			}
		]
	};

	const netActiveUsersCountOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: 'Net active users count'
			}
		}
	};

	const netActiveUsersCountData = {
		labels,
		datasets: [
			{
				lineTension: 0.3,
				fill: true,
				data: getNetActiveUsersCount(),
				borderColor: 'rgb(255, 230, 122)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(255, 230, 122,0.6)');
					gradient.addColorStop(1, 'rgba(255, 230, 122,0.03)');
					return gradient;
				}
			}
		]
	};

	const createdTodoItemsCountOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: 'Created Todo Items Count'
			}
		}
	};

	const createdTodoItemsCountData = {
		labels,
		datasets: [
			{
				lineTension: 0.3,
				fill: true,
				data: getCreatedTodoItemsCount(),
				borderColor: 'rgb(53, 102, 235)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(53, 102, 235,0.6)');
					gradient.addColorStop(1, 'rgba(53, 102, 235,0.03)');
					return gradient;
				}
			}
		]
	};

	const createdNotesCountOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: 'Created Notes Count'
			}
		}
	};

	const createdNotesCountData = {
		labels,
		datasets: [
			{
				lineTension: 0.3,
				fill: true,
				data: getCreatedNotesCount(),
				borderColor: 'rgb(33, 202, 155)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(33, 202, 155,0.6)');
					gradient.addColorStop(1, 'rgba(33, 202, 155,0.03)');
					return gradient;
				}
			}
		]
	};

	const installCountOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: 'Install Count'
			}
		}
	};

	const installCountData = {
		labels,
		datasets: [
			{
				lineTension: 0.3,
				fill: true,
				data: getInstallsCount(),
				borderColor: 'rgb(255, 200, 52)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(255, 200, 52,0.6)');
					gradient.addColorStop(1, 'rgba(255, 200, 52,0.03)');
					return gradient;
				}
			}
		]
	};

	const uninstallCountOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: 'Uninstall Count'
			}
		}
	};

	const uninstallCountData = {
		labels,
		datasets: [
			{
				lineTension: 0.3,
				fill: true,
				data: getUninstallsCount(),
				borderColor: 'rgb(255, 49, 82)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(255, 49, 82,0.6)');
					gradient.addColorStop(1, 'rgba(255, 49, 82,0.03)');
					return gradient;
				}
			}
		]
	};

	const retentionRateOptions:
		| _DeepPartialObject<
				CoreChartOptions<'line'> &
					ElementChartOptions<'line'> &
					PluginChartOptions<'line'> &
					DatasetChartOptions<'line'> &
					ScaleChartOptions<'line'> &
					LineControllerChartOptions
		  >
		| undefined = {
		responsive: true,
		// interaction: { mode: 'point', axis: 'x', intersect: true },
		scales: {
			y: {
				max: 100,
				min: 0
			}
		},
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: `Average Retained Users percentage ${retentionRatePerPeriod?.retentionRate.toFixed(
					2
				)}% - Active Count: ${
					retentionRatePerPeriod?.activeUsersCount
				} - Inactive Count: ${retentionRatePerPeriod?.inactiveUsersCount}`
			}
		}
	};

	const retentionRateData: ChartData<
		'line',
		(number | undefined)[] | undefined,
		string
	> = {
		labels,
		datasets: [
			{
				tension: 0.3,
				fill: true,
				data: getRetentionRate(),
				label: `Daily Retention(%)`,
				borderColor: 'rgb(235, 109, 82)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(235, 109, 82,0.6)');
					gradient.addColorStop(1, 'rgba(235, 109, 82,0.03)');
					return gradient;
				}
			}
		]
	};

	const chartRef = useRef<null | HTMLCanvasElement>() as any;

	return (
		<>
			{isLoading ? (
				<LoadingAnimComponent />
			) : isError ? (
				<div className="h-screen ml-18 ">
					<div className="flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
						<div className="mx-auto my-auto self-center">
							<div className="min-h-16">
								<DataNotFoundAnimation height={dimensions.height / 1.5} />
							</div>
							<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
								{"Couldn't find the data you're looking for!"}
							</h3>
						</div>
					</div>
				</div>
			) : (
				<main className="h-full">
					<section className="py-10">
						<div className="mx-auto px-4 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl w-10/12 h-fit transition-all duration-500 ease-in-out">
							<h3 className="dark:text-gray-400 text-lg font-bold mt-2 ml-2 mb-1">
								Latest Statistics
							</h3>
							<div className="grid grid-cols-4 gap-4 px-5 pt-3 mb-3">
								<ItemCard
									title="Total users count"
									latestStats={latestStats.totalNumberOfUsers}
									periodicStats={periodicStats}
									period={period}
									getPercentageDiff={() =>
										getCountPercentageDiff(
											latestStats.totalNumberOfUsers,
											periodicStats,
											period,
											percentageType.TOTAL
										)
									}
									photo={users}
								/>
								<ItemCard
									title="Active users count"
									latestStats={latestStats.numberOfDailyActiveUsers}
									periodicStats={periodicStats}
									period={period}
									getPercentageDiff={() =>
										getCountPercentageDiff(
											latestStats.numberOfDailyActiveUsers,
											periodicStats,
											period,
											percentageType.ACTIVE
										)
									}
									photo={cycle}
								/>
								<ItemCard
									title="Inactive users count"
									latestStats={latestStats.inactiveUsersCount}
									periodicStats={periodicStats}
									period={period}
									getPercentageDiff={() =>
										getCountPercentageDiff(
											latestStats.inactiveUsersCount,
											periodicStats,
											period,
											percentageType.INACTIVE_COUNT
										)
									}
									photo={cycle}
								/>
								<ItemCard
									title="Inactive users average time (days)"
									latestStats={latestStats.inactiveUsersAvgTime}
									periodicStats={periodicStats}
									period={period}
									getPercentageDiff={() =>
										getCountPercentageDiff(
											latestStats.inactiveUsersAvgTime,
											periodicStats,
											period,
											percentageType.INACTIVE_AVG_TIME
										)
									}
									photo={cycle}
								/>

								{showMore && (
									<>
										<ItemCard
											title="Net active users count"
											latestStats={latestStats.netActiveUsersCount}
											periodicStats={periodicStats}
											period={period}
											getPercentageDiff={() =>
												getCountPercentageDiff(
													latestStats.netActiveUsersCount,
													periodicStats,
													period,
													percentageType.NOTES_COUNT
												)
											}
											photo={cycle}
										/>
										<ItemCard
											title="Created Todo items count"
											latestStats={latestStats.createdTodoItemsCount}
											periodicStats={periodicStats}
											period={period}
											getPercentageDiff={() =>
												getCountPercentageDiff(
													latestStats.createdTodoItemsCount,
													periodicStats,
													period,
													percentageType.ITEMS_TODO_COUNT
												)
											}
											photo={cycle}
										/>
										<ItemCard
											title="Created notes count"
											latestStats={latestStats.createdNotesCount}
											periodicStats={periodicStats}
											period={period}
											getPercentageDiff={() =>
												getCountPercentageDiff(
													latestStats.createdNotesCount,
													periodicStats,
													period,
													percentageType.NOTES_COUNT
												)
											}
											photo={cycle}
										/>

										{/* <TotalCountOfOpenedTabsCard
													stats={latestStats.openedTabsCount}
													percentage={32}
													isIncrease={true}
												/>
												<TotalTimeSpentOnTabsCard
													stats={latestStats.totalNumberOfUsers}
													percentage={21}
													isIncrease={false}
												/> */}
									</>
								)}
							</div>

							<div className="flex w-full justify-center">
								<button
									type="button"
									className="btn btn-outline btn-sm btn-primary mb-3"
									onClick={() => setShowMore(!showMore)}
								>
									{showMore ? 'Show less' : 'Show more'}
								</button>
							</div>
						</div>
					</section>

					<section className="px-14">
						<div className="flex w-full flex-col items-end">
							<div className="inline-flex rounded-md shadow-sm" role="group">
								<div
									className={`py-2 px-4 text-sm font-medium text-gray-900 rounded-l-lg border border-gray-900 dark:border-white transition-all duration-500 cursor-pointer
										${
											period === 'custom'
												? 'bg-purple-500 text-white hover:bg-purple-600'
												: 'dark:hover:bg-purple-400 hover:bg-gray-700 hover:text-white dark:text-white dark:hover:text-white'
										}`}
									onClick={() => setPeriod('custom')}
								>
									<input
										type="radio"
										name="periodCustom"
										id="custom"
										checked={period === 'custom'}
										hidden
									/>
									<label className=" cursor-pointer" htmlFor="custom">
										Custom
									</label>
								</div>
								<div
									className={`py-2 px-4 text-sm font-medium text-gray-900  border border-gray-900 dark:border-white transition-all duration-500 cursor-pointer
										${
											period === 'week'
												? 'bg-purple-500 text-white hover:bg-purple-600'
												: 'dark:hover:bg-purple-400 hover:bg-gray-700 hover:text-white dark:text-white dark:hover:text-white'
										}`}
									onClick={() => setPeriod('week')}
								>
									<input
										type="radio"
										name="periodWeek"
										id="week"
										checked={period === 'week'}
										hidden
									/>
									<label className=" cursor-pointer" htmlFor="week">
										Week
									</label>
								</div>

								<div
									className={`py-2 px-4 text-sm font-medium text-gray-900 border-t border-b border-l border-gray-900 dark:border-white transition-all duration-500 cursor-pointer
										${
											period === 'month'
												? 'bg-purple-500 text-white hover:bg-purple-600'
												: 'dark:hover:bg-purple-400 hover:bg-gray-700 hover:text-white dark:text-white dark:hover:text-white'
										}`}
									onClick={() => setPeriod('month')}
								>
									<input
										type="radio"
										name="periodMonth"
										id="month"
										checked={period === 'month'}
										hidden
									/>
									<label className=" cursor-pointer" htmlFor="month">
										Month
									</label>
								</div>

								<div
									className={`py-2 px-4 text-sm font-medium text-gray-900 border-t border-b border-l border-gray-900 dark:border-white transition-all duration-500 cursor-pointer
										${
											period === 'threeMonths'
												? 'bg-purple-500 text-white hover:bg-purple-600'
												: 'dark:hover:bg-purple-400 hover:bg-gray-700 hover:text-white dark:text-white dark:hover:text-white'
										}`}
									onClick={() => setPeriod('threeMonths')}
								>
									<input
										type="radio"
										name="periodThreeMonths"
										id="threeMonths"
										checked={period === 'threeMonths'}
										hidden
									/>
									<label className=" cursor-pointer" htmlFor="threeMonths">
										3 Months
									</label>
								</div>
								<div
									className={`py-2 px-4 text-sm font-medium text-gray-900 border-t border-b border-l border-gray-900 dark:border-white transition-all duration-500 cursor-pointer
										${
											period === 'sixMonths'
												? 'bg-purple-500 text-white hover:bg-purple-600'
												: 'dark:hover:bg-purple-400 hover:bg-gray-700 hover:text-white dark:text-white dark:hover:text-white'
										}`}
									onClick={() => setPeriod('sixMonths')}
								>
									<input
										type="radio"
										name="periodSixMonths"
										id="sixMonths"
										checked={period === 'sixMonths'}
										hidden
									/>
									<label className=" cursor-pointer" htmlFor="sixMonths">
										6 Months
									</label>
								</div>

								<div
									className={`py-2 px-4 text-sm font-medium text-gray-900 rounded-r-md border border-gray-900 dark:border-white transition-all duration-500 cursor-pointer
										${
											period === 'year'
												? 'bg-purple-500 text-white hover:bg-purple-600'
												: 'dark:hover:bg-purple-400 hover:bg-gray-700 hover:text-white dark:text-white dark:hover:text-white'
										}`}
									onClick={() => setPeriod('year')}
								>
									<input
										type="radio"
										name="periodYear"
										id="year"
										checked={period === 'year'}
										onChange={() => setPeriod('year')}
										hidden
									/>
									<label className=" cursor-pointer" htmlFor="year">
										Year
									</label>
								</div>
							</div>
							<div
								className={`flex flex-row justify-end items-end mt-2 space-x-2 transition-all duration-500 ${
									period === 'custom' ? 'opacity-100' : 'opacity-0'
								}`}
							>
								<div className="w-[170px] flex flex-col justify-end">
									<ReactDatePicker
										className="inputStyleDatePicker"
										selected={moment(startDate).toDate()}
										maxDate={moment(endDate).subtract(1, 'days').toDate()}
										onChange={(date) => {
											if (moment(date) < moment(endDate))
												setStartDate(moment(date).format('MM/DD/YYYY'));
										}}
										dateFormat="MMMM d, yyyy"
										fixedHeight
									/>
									<p className="text-gray-600 self-end dark:text-gray-500 text-xs italic">
										Start date
									</p>
								</div>
								<div className="w-[170px] flex flex-col justify-end">
									<ReactDatePicker
										className="inputStyleDatePicker"
										selected={moment(endDate).toDate()}
										minDate={moment(startDate).add(1, 'days').toDate()}
										onChange={(date) => {
											if (moment(date) > moment(startDate))
												setEndDate(moment(date).format('MM/DD/YYYY'));
										}}
										dateFormat="MMMM d, yyyy"
										fixedHeight
									/>
									<p className="text-gray-600 self-end dark:text-gray-500 text-xs italic">
										End date
									</p>
								</div>
							</div>
						</div>

						{isFetching ? (
							<LoadingAnimComponent height={300} />
						) : (
							<div
								className={`${
									period === 'custom' ? 'translate-y-3' : '-translate-y-12'
								} transition-all duration-500`}
							>
								{dynamicStats != null &&
									moment(endDate).diff(moment(startDate), 'days') < 100 &&
									moment(endDate).diff(moment(startDate), 'days') > 30 && (
										<div
											className="px-4 py-2 rounded-xl dark:shadow-lg shadow-xl transition-all
									   		   duration-500 mb-10 overflow-hidden"
										>
											<CohortsView
												cohorts={dynamicStats.retentionCohorts}
												endDate={moment(endDate)}
											/>
										</div>
									)}

								<div
									className={`grid grid-cols-2 r mx-auto w-full transition-all duration-500 ${
										period === 'custom' ? 'mt-0' : '-mt-0'
									}`}
								>
									<div className="px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											options={totalNumberOfUsersOptions}
											data={totalNumberOfUsersData}
										/>
									</div>
									<div className="ml-3 px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											options={numberOfDailyActiveUsersOptions}
											data={numberOfDailyActiveUsersData}
										/>
									</div>
									<div className="px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											options={inactiveUsersCountOptions}
											data={inactiveUsersCountData}
										/>
									</div>
									<div className="ml-3 px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											ref={chartRef}
											// onClick={(event) => {
											// 	const dataset = getDatasetAtEvent(
											// 		chartRef.current,
											// 		event
											// 	);
											// 	const element = getElementAtEvent(
											// 		chartRef.current,
											// 		event
											// 	);
											// 	const elements = getElementsAtEvent(
											// 		chartRef.current,
											// 		event
											// 	);

											// 	console.log(element);
											// }}
											options={retentionRateOptions}
											data={retentionRateData}
										/>
									</div>
									<div className="ml-3 px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											options={inactiveUsersAvgTimeOptions}
											data={inactiveUsersAvgTimeData}
										/>
									</div>

									<div className="ml-3 px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											options={netActiveUsersCountOptions}
											data={netActiveUsersCountData}
										/>
									</div>
									<div className="px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											options={uninstallCountOptions}
											data={uninstallCountData}
										/>
									</div>
									<div className="ml-3 px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											options={installCountOptions}
											data={installCountData}
										/>
									</div>
									<div className="ml-3 px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											options={createdNotesCountOptions}
											data={createdNotesCountData}
										/>
									</div>
									<div className="ml-3 px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											options={createdTodoItemsCountOptions}
											data={createdTodoItemsCountData}
										/>
									</div>
								</div>
							</div>
						)}
					</section>
				</main>
			)}
		</>
	);
}

type LatestStatsProps = {
	latestStats: number;
	periodicStats: any[];
	period: string;
	percentage?: number;
	isIncrease?: boolean;
	photo: string;
	getPercentageDiff: () => PercentageType;
	title: string;
};

type PercentageType = { percentage: number; isIncrease: boolean };

enum percentageType {
	ACTIVE = 'active',
	NET_ACTIVE = 'netActive',
	TOTAL = 'total',
	INACTIVE_COUNT = 'inactiveCount',
	INACTIVE_AVG_TIME = 'inactiveAvgTime',
	NOTES_COUNT = 'createdNotesCount',
	ITEMS_TODO_COUNT = 'createdTodoItemsCount'
}

const getCountPercentageDiff = (
	latestStats: number,
	periodicStats: DailyStatsType[],
	period: string,
	perType = percentageType.TOTAL
): PercentageType => {
	try {
		let previousStat = periodicStats[periodicStats.length - 2];

		if (period === 'month') {
			previousStat = periodicStats[periodicStats.length - 8];
		}

		let onePrecentofLatestValue;
		let previousValue;
		let currentValue;

		if (perType === percentageType.TOTAL) {
			onePrecentofLatestValue = previousStat.totalNumberOfUsers / 100;
			previousValue = previousStat.totalNumberOfUsers;
			currentValue = latestStats;
		} else if (perType === percentageType.INACTIVE_COUNT) {
			onePrecentofLatestValue = previousStat.inactiveUsersCount / 100;
			previousValue = previousStat.inactiveUsersCount;
			currentValue = latestStats;
		} else if (perType === percentageType.INACTIVE_AVG_TIME) {
			onePrecentofLatestValue = previousStat.inactiveUsersAvgTime / 100;
			previousValue = previousStat.inactiveUsersAvgTime;
			currentValue = latestStats;
		} else if (perType === percentageType.NET_ACTIVE) {
			onePrecentofLatestValue = previousStat.netActiveUsersCount / 100;
			previousValue = previousStat.netActiveUsersCount;
			currentValue = latestStats;
		} else if (perType === percentageType.NOTES_COUNT) {
			onePrecentofLatestValue = previousStat.createdNotesCount / 100;
			previousValue = previousStat.createdNotesCount;
			currentValue = latestStats;
		} else if (perType === percentageType.ITEMS_TODO_COUNT) {
			onePrecentofLatestValue = previousStat.createdTodoItemsCount / 100;
			previousValue = previousStat.createdTodoItemsCount;
			currentValue = latestStats;
		} else {
			onePrecentofLatestValue = previousStat.numberOfDailyActiveUsers / 100;
			previousValue = previousStat.numberOfDailyActiveUsers;
			currentValue = latestStats;
		}

		const percentage = (currentValue - previousValue) / onePrecentofLatestValue;

		const isIncrease = percentage >= 0 ? true : false;

		return {
			percentage: parseFloat(percentage.toFixed(2)),
			isIncrease: isIncrease
		};
	} catch (e) {
		return {
			percentage: 0,
			isIncrease: true
		};
	}
};

const ItemCard = ({
	latestStats,
	period,
	getPercentageDiff,
	photo,
	title: text
}: LatestStatsProps) => {
	const [percentageDiff, setPercentageDiff] =
		useState<PercentageType>(getPercentageDiff);

	useEffect(() => {
		setPercentageDiff(getPercentageDiff);
	}, [period]);

	return (
		<div className="flex rounded-lg h-full justify-center">
			<div className="relative flex flex-row h-20 my-auto">
				<div className="mt-0.5">
					<LazyImage photo={photo} dimesnions={'68'} loaderDimesnions={68} />
				</div>
				<div className="flex flex-col ml-3 w-fit h-full mt-0.5">
					<h3 className="dark:text-gray-400 text-xs font-bold text-center">
						{text}
					</h3>
					<h3 className="dark:text-white text-3xl font-bold text-center">
						{latestStats.toFixed(0)}
					</h3>

					<div className="flex flex-row w-full justify-center">
						<h3
							className={`text-xs font-bold text-center mt-2 mr-1 ${
								percentageDiff.isIncrease ? 'text-green-600' : 'text-red-600'
							}`}
						>
							{!isNaN(percentageDiff.percentage)
								? `${percentageDiff.isIncrease ? '+' : ''}${
										percentageDiff.percentage
								  }%`
								: 'N/A'}
						</h3>
						<h3 className="dark:text-gray-400 text-xs font-bold text-center mt-2">
							{!isNaN(percentageDiff.percentage)
								? `${percentageDiff.isIncrease ? 'Increase' : 'Decrease'}`
								: ''}
						</h3>
					</div>
				</div>
			</div>
		</div>
	);
};
